"use client";
import * as React from "react";
import { Suspense, lazy, useEffect } from "react";
import { useLocation, useNavigationType, createRoutesFromChildren, matchRoutes } from "react-router";
import { createRoot } from "react-dom/client";
import {
  createBrowserRouter,
  Navigate,
  useLocation as useRouterLocation,
} from "react-router-dom";
import './index.css';
import reportWebVitals from './reportWebVitals';
import { initializeApp } from "firebase/app";

import { initializeAnalytics } from "firebase/analytics";
import {getWindowDimensions} from './player/Utils';
import * as Sentry from "@sentry/react";
import i18next from './i18n'; // DO NOT DELETE
// Optimisation : charger uniquement les poids de police utilisés
import '@fontsource/roboto/400.css'; // Regular
import '@fontsource/roboto/700.css'; // Bold
import "@fontsource/roboto-condensed/400.css"; // Regular condensed
import AnalyticsManager from "./analytics/AnalyticsManager";
import { getFunctions } from "firebase/functions";

// Composants principaux et fréquemment utilisés - imports statiques (chargés immédiatement)
import InstantAdHP from './InstantAdHP';
import Player from './player/Player'; // Composant principal, utilisé partout - reste dans main.js
import AppRoot from "./AppRoot";
import ErrorPage from "./ErrorPage";
import ErrorPageWithRedirect from "./ErrorPageWithRedirect";
import RouteLoading from "./components/RouteLoading";
import MakerLoading from "./components/MakerLoading";
import SimulatorLoading from "./components/SimulatorLoading";
import BookingMock from "./pages/BookingMock";

// Code splitting - imports lazy avec noms explicites (chargés à la demande)
const Maker = lazy(() => import(/* webpackChunkName: "maker" */ './maker/Maker'));
const Logged = lazy(() => import(/* webpackChunkName: "logged" */ './logged/Logged'));
const Pricing = lazy(() => import(/* webpackChunkName: "pricing" */ './Pricing'));
const SalesDeck = lazy(() => import(/* webpackChunkName: "sales-deck" */ './HP/SalesDeck'));
const InstagramFeedSimulator = lazy(() => import(/* webpackChunkName: "ig-simulator" */ './InstagramFeedSimulator'));
const InstagramFeedSimulator2 = lazy(() => import(/* webpackChunkName: "ig-simulator-2" */ './InstagramFeedSimulator2'));
const DemoFeed = lazy(() => import(/* webpackChunkName: "demo-feed" */ './pages/DemoFeed'));
const UseCasesDemo = lazy(() => import(/* webpackChunkName: "usecases-demo" */ './pages/UseCasesDemo'));
const InputDesign = lazy(() => import(/* webpackChunkName: "input-design" */ './lovable/v2/InputDesign'));
const PrivacyPolicy = lazy(() => import(/* webpackChunkName: "privacy" */ './pages/PrivacyPolicy'));
const VideoTest = lazy(() => import(/* webpackChunkName: "video-test" */ './pages/VideoTest'));
const StorefrontVideoDev = process.env.NODE_ENV === 'development'
  ? lazy(() => import(/* webpackChunkName: "storefront-video-dev" */ './pages/StorefrontVideoDev'))
  : null;
const AboutPage = lazy(() => import(/* webpackChunkName: "about" */ './pages/AboutPage'));
const RestaurantToolsHub = lazy(() => import(/* webpackChunkName: "restaurant-tools" */ './tools/RestaurantToolsHub'));
const RestaurantToolPage = lazy(() => import(/* webpackChunkName: "restaurant-tool" */ './tools/RestaurantToolPage'));
Sentry.init({
  dsn: "https://fec78e2fbae74d429a654c6793d609c3@o1178872.ingest.sentry.io/6291166",
  replaysOnErrorSampleRate: 1.0,
  integrations: [
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect: React.useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
    Sentry.replayIntegration({   
      maskAllText: false,
      blockAllMedia: false,   
    }),
  ],
  environment: process.env.NODE_ENV,
  beforeSend(event, hint) {
    // redirect to the website if the app launches an exception    
    /*setTimeout(()=>{      
      if (event.level !== "info" && webFallback){
        window.location = webFallback;
      }
    }, 1000);*/

    // Filtrer les erreurs liées aux WebViews Android/iOS qui ne sont pas contrôlables
    if (event.exception && event.exception.values) {
      const errorMessage = event.exception.values[0]?.value || '';
      const errorType = event.exception.values[0]?.type || '';
      
      // Erreur WebView Android Instagram
      if (errorMessage.includes('Java object is gone') || 
          errorMessage.includes('enableDidUserTypeOnKeyboardLogging')) {
        return null; // Ne pas envoyer cette erreur à Sentry
      }
      
      // Erreur IndexedDB dans les WebViews in-app iOS (Facebook/Instagram)
      // Se produit quand iOS suspend/vide IndexedDB après mise en background prolongée
      // Firestore utilise IndexedDB pour le cache local
      if (errorMessage.includes('Connection to Indexed Database server lost') ||
          (errorMessage.includes('IndexedDB') && errorMessage.includes('server lost'))) {
        return null; // Ne pas envoyer cette erreur à Sentry
      }
      
      // Erreur IndexedDB supprimée par iOS
      // iOS supprime IndexedDB des WebViews in-app pour libérer de la mémoire
      // Firestore recrée automatiquement la base et resynchronise
      if (errorMessage.includes('Database deleted by request of the user')) {
        return null; // Ne pas envoyer cette erreur à Sentry
      }
      
      // FirebaseError "client is offline" dans les WebViews in-app
      // Se produit quand les restrictions réseau du navigateur bloquent Firestore
      // Firestore gère automatiquement ces erreurs (retry, cache local)
      if (errorType === 'FirebaseError' && 
          errorMessage.includes('client is offline')) {
        return null; // Ne pas envoyer cette erreur à Sentry
      }
      
      // FirebaseError "app-offline" - Firebase Installations
      // Se produit quand l'appareil est offline au moment de l'enregistrement
      // Firebase retry automatiquement quand la connexion revient
      if (errorType === 'FirebaseError' && 
          (errorMessage.includes('app-offline') || 
           errorMessage.includes('Application offline'))) {
        return null; // Ne pas envoyer cette erreur à Sentry
      }
      
      // FirebaseError "network-request-failed" - UNIQUEMENT Firebase Auth
      // Se produit quand les restrictions réseau du navigateur bloquent Firebase Auth
      // Firebase retry automatiquement
      // Important: filtrer UNIQUEMENT si ça vient de @firebase/auth (pas Storage, Functions, etc.)
      if (errorType === 'FirebaseError' && 
          errorMessage.includes('auth/network-request-failed')) {
        return null; // Ne pas envoyer cette erreur à Sentry
      }
      
      // Erreur WebKit messageHandlers dans les WebViews in-app iOS (Facebook/Instagram)
      // Se produit quand les WebViews tentent de communiquer avec l'app native
      // mais les messageHandlers ne sont pas disponibles ou configurés
      if (errorMessage.includes('window.webkit.messageHandlers') && 
          errorMessage.includes('postMessage')) {
        return null; // Ne pas envoyer cette erreur à Sentry
      }

      // Bot / HeadlessChrome (UtilityScript) : itère window.frames hors de notre code
      // JACQUES-GIRAUDEL-CONSULTING-32R
      if (errorMessage.includes('window.frames is not iterable')) {
        return null;
      }
    }

    if (event.message && event.message.includes("error leading to a redirect")) {
      return null;
    }

    return event;
  },
  tracesSampleRate: 1.0,
  tracePropagationTargets: ["localhost", /^https:\/\/upreality\.store\//, /^https:\/\/www\.upreality\.store\//],
});

const firebaseConfig = {
  apiKey: process.env.REACT_APP_GOOGLE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,  
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_APP_ID,
  measurementId: process.env.REACT_APP_MEASUREMENT_ID
};
//databaseURL: "https://upreality-1550998227636.firebaseio.com",

export var webFallback = null;
function setWebFallback(fallback){
  if (process.env.NODE_ENV && process.env.NODE_ENV !== 'development' && fallback) {        
    if (fallback.includes("?")){
      webFallback = fallback + window.location.search.replace("?","&");
    }
    else {
      webFallback = fallback + window.location.search;
    }
  }
}
export {setWebFallback};
export const app = initializeApp(firebaseConfig);
export const functions = getFunctions(app, "europe-west3");
// DemoFeed charge 2 iframes = 2 boots SPA : pas de page_view auto dans les cadres
const analyticsInIframe = typeof window !== "undefined" && window.self !== window.top;
export const analytics = initializeAnalytics(app, {
  config: { send_page_view: !analyticsInIframe },
});
export const analyticsManager = new AnalyticsManager(analytics);
export const testedGroup = Math.random() < 0.5 ? false : true;
analyticsManager.setUserProperty(testedGroup, null);

const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouter(createBrowserRouter);

// Helper pour wrapper les composants lazy avec Suspense
const LazyRoute = ({ children }) => (
  <Suspense fallback={<RouteLoading />}>
    {children}
  </Suspense>
);

// Helper spécifique pour Maker avec background #b2ede7
const LazyMakerRoute = ({ children }) => (
  <Suspense fallback={<MakerLoading />}>
    {children}
  </Suspense>
);

const SimulatorLazyRoute = ({ children }) => (
  <Suspense fallback={<SimulatorLoading />}>
    {children}
  </Suspense>
);

const DemoFeedLazyRoute = ({ children }) => (
  <Suspense fallback={<div style={{ minHeight: "100vh", width: "100vw", backgroundColor: "#f7f9fc" }} />}>
    {children}
  </Suspense>
);

const RootElement = () => {

    // for restaurant domain redirect (see htaccess)  
    function returnStoryIdToRedirect(){
      let storyId = null;
      if (window.location.hostname === "social.job-nutriscore.eu") {
        storyId = "LoulouRestaurantParis";
      }
      return storyId;
    } 
    
    const storyIdToRedirect = returnStoryIdToRedirect();      
    if (storyIdToRedirect !== null) {
      return <Player storyId={storyIdToRedirect} fromMaker={false} windowDimensions={getWindowDimensions()} />;
    }
    else {  
      return <InstantAdHP windowDimensions={getWindowDimensions()}/>;
    }
};

const PlayerPage = () => {
  const searchParams = new URLSearchParams(window.location.search);
  const miniFlag = (searchParams.get("mini") || "").toLowerCase();
  const mini = miniFlag === "1" || miniFlag === "true" || miniFlag === "yes";
  return <Player fromMaker={false} mini={mini || undefined} windowDimensions={getWindowDimensions()} />;
};

const normalizeLanguage = (lng) => {
  const value = (lng || "").toLowerCase();
  return value.startsWith("fr") ? "fr" : "en";
};

const getPreferredLanguage = () => {
  return normalizeLanguage(i18next.resolvedLanguage || i18next.language || (typeof navigator !== "undefined" ? navigator.language : "en"));
};

const LanguageRoute = ({ lang, children }) => {
  useEffect(() => {
    if (i18next.language !== lang) {
      i18next.changeLanguage(lang);
    }
  }, [lang]);

  return children;
};

const RedirectToLocalizedHome = () => {
  const location = useRouterLocation();
  return <Navigate to={`/${getPreferredLanguage()}${location.search || ""}`} replace />;
};

const RedirectToLocalizedAbout = () => {
  const location = useRouterLocation();
  return <Navigate to={`/${getPreferredLanguage()}/about${location.search || ""}`} replace />;
};

const router = sentryCreateBrowserRouter([
  {
    path: "/",
    element: <RedirectToLocalizedHome />,
    errorElement: <ErrorPageWithRedirect />, 
  },
  {
    path: "/about",
    element: <RedirectToLocalizedAbout />,
    errorElement: <ErrorPage />,
  },
  {
    path: "/fr",
    element: <LanguageRoute lang="fr"><RootElement /></LanguageRoute>,
    errorElement: <ErrorPageWithRedirect />,
  },
  {
    path: "/en",
    element: <LanguageRoute lang="en"><RootElement /></LanguageRoute>,
    errorElement: <ErrorPageWithRedirect />,
  },
  {
    path: "/fr/about",
    element: (
      <LanguageRoute lang="fr">
        <LazyRoute>
          <AboutPage />
        </LazyRoute>
      </LanguageRoute>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: "/en/about",
    element: (
      <LanguageRoute lang="en">
        <LazyRoute>
          <AboutPage />
        </LazyRoute>
      </LanguageRoute>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: "Pricing",
    element: (
      <LazyRoute>
        <Pricing windowDimensions={getWindowDimensions()} />
      </LazyRoute>
    ),
    errorElement: <ErrorPage />, 
  },
  {
    path: "logged/:page?",
    element: (
      <LazyRoute>
        <Logged />
      </LazyRoute>
    ),
    errorElement: <ErrorPage />, 
  }, 
  {
    path: "maker/:storyId?",
    element: (
      <LazyMakerRoute>
        <Maker />
      </LazyMakerRoute>
    ),
    errorElement: <ErrorPage />, 
  }, 
  {
    path: "p/:storyId",
    element: <PlayerPage />,
    errorElement: <ErrorPageWithRedirect />,
  },
  {
    path: "/booking/:storyId",
    element: <BookingMock />,
    errorElement: <ErrorPage />,
  },
  {
    path: "/InstagramFeedSimulator",
    element: (
      <SimulatorLazyRoute>
        <InstagramFeedSimulator />
      </SimulatorLazyRoute>
    ),
    errorElement: <ErrorPage />, 
  },
  {
    path: "/InstagramFeedSimulator2",
    element: (
      <SimulatorLazyRoute>
        <InstagramFeedSimulator2 />
      </SimulatorLazyRoute>
    ),
    errorElement: <ErrorPage />, 
  },
  {
    path: "/DemoFeed",
    element: (
      <DemoFeedLazyRoute>
        <DemoFeed />
      </DemoFeedLazyRoute>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: "/usecasesdemo",
    element: (
      <DemoFeedLazyRoute>
        <UseCasesDemo />
      </DemoFeedLazyRoute>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: "/Demo",
    element: (
      <LazyRoute>
        <SalesDeck />
      </LazyRoute>
    ),
    errorElement: <ErrorPage />, 
  },
  {
    path: "/Editor",
    element: (
      <LazyRoute>
        <InputDesign />
      </LazyRoute>
    ),
    errorElement: <ErrorPage />, 
  },
  {
    path: "/PrivacyPolicy",
    element: (
      <LazyRoute>
        <PrivacyPolicy />
      </LazyRoute>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: "/VideoTest",
    element: (
      <LazyRoute>
        <VideoTest />
      </LazyRoute>
    ),
    errorElement: <ErrorPage />,
  },
  ...(StorefrontVideoDev
    ? [{
        path: "/dev/storefront-video",
        element: (
          <LazyRoute>
            <StorefrontVideoDev />
          </LazyRoute>
        ),
        errorElement: <ErrorPage />,
      }]
    : []),
  {
    path: "/outils-restaurants",
    element: (
      <LazyRoute>
        <RestaurantToolsHub />
      </LazyRoute>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: "/outils-restaurants/:toolSlug",
    element: (
      <LazyRoute>
        <RestaurantToolPage />
      </LazyRoute>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: "*",
    element: <RedirectToLocalizedHome />,
  },
  
]);

startApp();

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();

function startApp(){
  createRoot(document.getElementById("root")).render(      
      <AppRoot router={router}/>     
  );
}
